import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ScheduledPost } from '@/types'

export function useScheduledPosts() {
  return useQuery<ScheduledPost[]>({
    queryKey: ['poster'],
    queryFn: () => api.get('/poster'),
  })
}

export interface ScheduleLimit {
  limit: number | null
  current: number
  remaining: number | null
  unlimited: boolean
}

export function useScheduleLimit() {
  return useQuery<ScheduleLimit>({
    queryKey: ['schedule-limit'],
    queryFn: () => api.get('/poster/schedule-limit'),
    staleTime: 30_000,
  })
}

export function usePublishPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      accounts: Array<{ igUserId: string; platforms: string[] }>
      title?: string
      message: string
      imageUrl?: string
      scheduleAt?: string
      action: 'now' | 'schedule'
      category?: string
      mediaType?: 'image' | 'video'
      postType?: 'feed' | 'story'
      storyLink?: string
      youtubeChannels?: string[]
      youtubeTitle?: string
      youtubeDescription?: string
      youtubeTags?: string[]
      youtubePrivacy?: string
      preferredProfile?: string
      antiFingerprint?: boolean
      ctaSendMessage?: boolean
    }) => api.post<{
      ok: boolean
      results: Array<{ igUserId?: string; youtubeChannelId?: string; platform: string; published?: boolean; scheduled?: boolean; error?: string }>
      batchId: string
    }>('/poster/publish', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export function useEditPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ postId, ...data }: {
      postId: string
      title?: string
      message?: string
      imageUrl?: string
      scheduleAt?: string
      category?: string
    }) => api.put<{ ok: boolean; post: ScheduledPost }>(`/poster/${postId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export function useDeletePost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (postId: string) => api.delete(`/poster/${postId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      contentItems: Array<{ id: string; message: string; imageUrl: string; mediaType: 'image' | 'video' }>
      accounts: Array<{ igUserId: string; platforms: string[] }>
      scheduleDate?: string
      startTime?: string
      endTime?: string
      intervalMinutes?: number
      includeStories?: boolean
      storyContentIndex?: number
      category?: string
      multiDay?: boolean
      imagesPerDay?: number
      intervalMin?: number
      intervalMax?: number
      startDate?: string
      totalDays?: number
      postTime?: string
      fromLibrary?: boolean
      folderId?: string
      imageCount?: number
      recyclable?: boolean
      selectedImageIds?: string[]
      forceRecycle?: boolean
      captionTone?: string
      libraryCaptions?: Record<string, string>
      customCaption?: string
      clipMode?: boolean
      clipMorningTime?: string
      clipAfternoonTime?: string
      clipDistribution?: 'sequential' | 'shuffled'
      youtubeChannels?: string[]
      youtubeTitle?: string
      youtubeDescription?: string
      youtubeTags?: string[]
      youtubePrivacy?: string
      preferredProfile?: string
      antiFingerprint?: boolean
      ctaSendMessage?: boolean
    }) => api.post<{
      ok: boolean
      campaignId: string
      totalPosts: number
      storyPosts: number
      feedPosts: number
      ytPosts?: number
      pagesCount: number
    }>('/poster/campaign', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['poster'] })
      qc.invalidateQueries({ queryKey: ['folder-stats'] })
    },
  })
}

export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (campaignId: string) =>
      api.delete<{ ok: boolean; removed: number }>(`/poster/campaign/${campaignId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export function useClearPublished() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params?: { statuses?: string[]; types?: string[]; pageId?: string }) =>
      api.delete<{ ok: boolean; removed: number }>('/poster/clear/published', params ? {
        ...(params.statuses ? { statuses: params.statuses } : {}),
        ...(params.types ? { types: params.types } : {}),
        ...(params.pageId ? { page_id: params.pageId } : {}),
      } : undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export function useClearPending() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { pageId: string; types?: string[] }) =>
      api.delete<{ ok: boolean; removed: number }>('/poster/clear/pending', {
        page_id: params.pageId,
        ...(params.types ? { types: params.types } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['poster'] }),
  })
}

export interface MediaUsageEntry {
  accountName: string
  pageId: string
  platform: string
  scheduledAt: number
  status: string
}

export function useMediaUsage(urls: string[]) {
  return useQuery<Record<string, MediaUsageEntry[]>>({
    queryKey: ['media-usage', urls],
    queryFn: () => api.post('/poster/media-usage', { urls }),
    enabled: urls.length > 0,
    staleTime: 60_000,
  })
}

export interface FolderUsageEntry {
  count: number
  accountName: string
  lastUsed: number
}

// Returns { folderId: { pageId: { count, accountName, lastUsed } } }
export function useFolderUsage(folderIds: string[]) {
  return useQuery<Record<string, Record<string, FolderUsageEntry>>>({
    queryKey: ['folder-usage', folderIds],
    queryFn: () => api.post('/poster/folder-usage', { folderIds }),
    enabled: folderIds.length > 0,
    staleTime: 60_000,
  })
}

export interface PublishHistoryItem {
  campaignId: string
  category: string
  publishedAt: number
  totalPosts: number
  feedPosts: number
  storyPosts: number
  videoPosts: number
  imagePosts: number
  accounts: string[]
  platforms: string[]
}

export function usePublishHistory() {
  return useQuery<PublishHistoryItem[]>({
    queryKey: ['publish-history'],
    queryFn: () => api.get('/poster/publish-history'),
  })
}

export interface CampaignDetailPost {
  id: string
  imageUrl: string
  mediaType: 'image' | 'video'
  postType: 'feed' | 'story'
  message: string
  accountName: string
  accounts: string[]
  platform: string
  publishedAt: number
  status: string
}

export function useCampaignDetail(campaignId: string | null) {
  return useQuery<CampaignDetailPost[]>({
    queryKey: ['campaign-detail', campaignId],
    queryFn: () => api.get(`/poster/publish-history/${campaignId}`),
    enabled: !!campaignId,
  })
}
